import React from 'react';

import {
  LinkIcon,
  NoneIcon,
  PhoneWithArrowPointingInwardsIcon,
  PhoneWithArrowPointingOutwardsIcon,
  TextIcon,
} from '@modernloop/shared/icons';
import { GoogleMeetLogo, MicrosoftTeamsLogo, ZoomLogo } from '@modernloop/shared/images';
import { Avatar } from '@mui/material';

import {
  DynamicInterviewMeetingHost,
  InterviewMeetingLocationType,
  VideoMeetingLinkScope,
} from 'src/generated/mloop-graphql';

// Note: This utility function returns untranslated labels for backward compatibility.
// Callers that need translated labels should use t() directly from their component context.
export const getInterviewMeetingLocationLabel = (interviewMeetingLocationType?: InterviewMeetingLocationType) => {
  switch (interviewMeetingLocationType) {
    case InterviewMeetingLocationType.Zoom: {
      return 'Zoom';
    }
    case InterviewMeetingLocationType.GoogleMeet: {
      return 'Google Meet';
    }
    case InterviewMeetingLocationType.MicrosoftTeams: {
      return 'Microsoft Teams';
    }
    case InterviewMeetingLocationType.CustomLink: {
      return 'Custom link';
    }
    case InterviewMeetingLocationType.CandidatePhone: {
      return 'Candidate phone number';
    }
    case InterviewMeetingLocationType.CustomPhone: {
      return 'Custom phone number';
    }
    case InterviewMeetingLocationType.CustomText: {
      return 'Custom text';
    }
    case InterviewMeetingLocationType.None: {
      return 'None';
    }

    default:
      return '';
  }
};

// Returns the i18n key (namespace:key) for a meeting location label so callers can render a
// translated label via t(). Brand names (Zoom, Google Meet, Microsoft Teams) map to keys whose
// values are intentionally identical across locales. Returns '' for unknown/unset types.
export const getInterviewMeetingLocationLabelKey = (
  interviewMeetingLocationType?: InterviewMeetingLocationType
): string => {
  switch (interviewMeetingLocationType) {
    case InterviewMeetingLocationType.Zoom:
      return 'hiring-dashboard-common:common.location.zoom';
    case InterviewMeetingLocationType.GoogleMeet:
      return 'hiring-dashboard-common:common.location.googleMeet';
    case InterviewMeetingLocationType.MicrosoftTeams:
      return 'hiring-dashboard-common:common.location.microsoftTeams';
    case InterviewMeetingLocationType.CustomLink:
      return 'hiring-dashboard-common:common.location.customLink';
    case InterviewMeetingLocationType.CandidatePhone:
      return 'hiring-dashboard-common:common.location.candidatePhone';
    case InterviewMeetingLocationType.CustomPhone:
      return 'hiring-dashboard-common:common.location.customPhone';
    case InterviewMeetingLocationType.CustomText:
      return 'hiring-dashboard-common:common.location.customText';
    case InterviewMeetingLocationType.None:
      return 'hiring-dashboard-common:common.none';
    default:
      return '';
  }
};

/**
 * Whether a meeting-location pref has never been configured. Prefs are stored as protobuf, so an
 * unconfigured pref resolves to the default instance (`NOT_SPECIFIED`) rather than to null — this is
 * the common state for the debrief pref, which only recently gained a settings surface.
 */
export const isMeetingLocationSettingUnset = (interviewMeetingLocationType?: InterviewMeetingLocationType): boolean =>
  !interviewMeetingLocationType || interviewMeetingLocationType === InterviewMeetingLocationType.NotSpecified;

/**
 * Seed for the debrief location editor when nothing is stored yet: Zoom hosted by the scheduler.
 * This is the debrief analog of the schedule page's Zoom + Interviewers seed; `INTERVIEWERS` is
 * rejected by the backend for debriefs because a debrief is a single meeting with a single host.
 *
 * The scope is only here because the shared fragment type requires it; debrief saves omit the field.
 */
export const UNSET_DEBRIEF_MEETING_LOCATION_SETTING = Object.freeze({
  interviewMeetingLocationType: InterviewMeetingLocationType.Zoom,
  dynamicHost: DynamicInterviewMeetingHost.Scheduler,
  videoMeetingLinkScope: VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet,
} as const);

/** Read-only summary shown for a debrief location nobody has configured yet. */
export const UNSET_DEBRIEF_MEETING_LOCATION_DISPLAY = Object.freeze({
  interviewMeetingLocationType: InterviewMeetingLocationType.None,
  videoMeetingLinkScope: VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet,
} as const);

// Location types that carry a generated/pasted video meeting link and therefore support a
// "one link for all interviews" vs. "unique link per interview" scope (see VideoMeetingLinkScope).
// Phone/text/none locations have no link, so the scope setting is meaningless for them.
export const LINK_BEARING_MEETING_LOCATION_TYPES: InterviewMeetingLocationType[] = [
  InterviewMeetingLocationType.Zoom,
  InterviewMeetingLocationType.GoogleMeet,
  InterviewMeetingLocationType.MicrosoftTeams,
  InterviewMeetingLocationType.CustomLink,
];

/** Whether the given location type carries a video meeting link that a scope setting can apply to. */
export const isLinkBearingMeetingLocationType = (
  interviewMeetingLocationType?: InterviewMeetingLocationType
): boolean =>
  !!interviewMeetingLocationType && LINK_BEARING_MEETING_LOCATION_TYPES.includes(interviewMeetingLocationType);

// Self-schedule link creation does not honor the scope for custom links, so that surface only offers
// the setting for generated video-link providers.
export const SELF_SCHEDULE_VIDEO_MEETING_LINK_SCOPE_LOCATION_TYPES: InterviewMeetingLocationType[] = [
  InterviewMeetingLocationType.Zoom,
  InterviewMeetingLocationType.GoogleMeet,
  InterviewMeetingLocationType.MicrosoftTeams,
];

/** Whether self-schedule can honor a one-link vs. unique-link choice for the selected location. */
export const isSelfScheduleVideoMeetingLinkScopeLocationType = (
  interviewMeetingLocationType?: InterviewMeetingLocationType
): boolean =>
  !!interviewMeetingLocationType &&
  SELF_SCHEDULE_VIDEO_MEETING_LINK_SCOPE_LOCATION_TYPES.includes(interviewMeetingLocationType);

/**
 * Resolve the video-meeting-link scope to persist for a meeting-location payload, enforcing the
 * invariants the backend validators hard-reject: no scope on non-link locations (and, by extension,
 * non-video/debrief prefs), and interviewer-hosted locations always resolve to UNIQUE — never
 * ONE_LINK. Returns `undefined` when no scope should be sent so the field is omitted from the payload.
 */
export const resolveVideoMeetingLinkScope = ({
  enabled,
  interviewMeetingLocationType,
  dynamicHost,
  existingScope,
}: {
  enabled: boolean;
  interviewMeetingLocationType?: InterviewMeetingLocationType;
  dynamicHost?: DynamicInterviewMeetingHost | null;
  existingScope?: VideoMeetingLinkScope | null;
}): VideoMeetingLinkScope | undefined => {
  if (!enabled || !isLinkBearingMeetingLocationType(interviewMeetingLocationType)) {
    return undefined;
  }
  if (dynamicHost === DynamicInterviewMeetingHost.Interviewers) {
    return VideoMeetingLinkScope.UniqueLinkPerInterview;
  }
  return existingScope && existingScope !== VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet
    ? existingScope
    : undefined;
};

/** Resolve the video-meeting-link scope for self-schedule payloads, excluding unsupported custom links. */
export const resolveSelfScheduleVideoMeetingLinkScope = ({
  enabled,
  interviewMeetingLocationType,
  dynamicHost,
  existingScope,
}: {
  enabled: boolean;
  interviewMeetingLocationType?: InterviewMeetingLocationType;
  dynamicHost?: DynamicInterviewMeetingHost | null;
  existingScope?: VideoMeetingLinkScope | null;
}): VideoMeetingLinkScope | undefined => {
  if (!enabled || !isSelfScheduleVideoMeetingLinkScopeLocationType(interviewMeetingLocationType)) {
    return undefined;
  }
  if (dynamicHost === DynamicInterviewMeetingHost.Interviewers) {
    return VideoMeetingLinkScope.UniqueLinkPerInterview;
  }
  return existingScope && existingScope !== VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet
    ? existingScope
    : undefined;
};

/**
 * Resolve the scope for a wholesale write (the org JSON pref, which replaces the stored object
 * entirely). GraphQL mutations must NOT use this — they omit the field and let the backend preserve
 * on null.
 *
 * Mirrors the backend `InterviewMeetingLocationSettingPrefUtils.preserveVideoMeetingLinkScope`
 * normalization so the wholesale write stays consistent with GraphQL preservation:
 * - An explicit payload scope (flag-on edit) is returned unchanged.
 * - Otherwise the fetched scope is echoed through so a flag-off save preserves it — but cleared to
 *   NOT_SET when the outgoing host/type makes it contradictory (INTERVIEWERS host + ONE_LINK, or a
 *   non-link-bearing location type).
 * - Falls back to NOT_SET when nothing was set or previously stored.
 */
export const resolveWholesaleVideoMeetingLinkScope = ({
  payloadScope,
  fetchedScope,
  dynamicHost,
  interviewMeetingLocationType,
}: {
  payloadScope: VideoMeetingLinkScope | null | undefined;
  fetchedScope: VideoMeetingLinkScope | null | undefined;
  dynamicHost?: DynamicInterviewMeetingHost | null;
  interviewMeetingLocationType?: InterviewMeetingLocationType;
}): VideoMeetingLinkScope => {
  if (payloadScope) {
    return payloadScope;
  }
  if (!fetchedScope || fetchedScope === VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet) {
    return VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet;
  }
  const isInterviewerOneLinkContradiction =
    dynamicHost === DynamicInterviewMeetingHost.Interviewers && fetchedScope === VideoMeetingLinkScope.OneLink;
  if (isInterviewerOneLinkContradiction || !isLinkBearingMeetingLocationType(interviewMeetingLocationType)) {
    return VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet;
  }
  return fetchedScope;
};

export const getInterviewMeetingLocationIcon = (interviewMeetingLocationType?: InterviewMeetingLocationType) => {
  const dataTestId = `interview-meeting-location-icon-${interviewMeetingLocationType}`;
  switch (interviewMeetingLocationType) {
    case InterviewMeetingLocationType.Zoom: {
      return <Avatar src={ZoomLogo} data-testid={dataTestId} />;
    }
    case InterviewMeetingLocationType.GoogleMeet: {
      return <Avatar src={GoogleMeetLogo} data-testid={dataTestId} />;
    }
    case InterviewMeetingLocationType.MicrosoftTeams: {
      return <Avatar src={MicrosoftTeamsLogo} data-testid={dataTestId} />;
    }
    case InterviewMeetingLocationType.CustomLink: {
      return <LinkIcon data-testid={dataTestId} color="action" />;
    }
    case InterviewMeetingLocationType.CandidatePhone: {
      return <PhoneWithArrowPointingOutwardsIcon data-testid={dataTestId} color="action" />;
    }
    case InterviewMeetingLocationType.CustomPhone: {
      return <PhoneWithArrowPointingInwardsIcon data-testid={dataTestId} color="action" />;
    }
    case InterviewMeetingLocationType.CustomText: {
      return <TextIcon data-testid={dataTestId} color="action" />;
    }
    case InterviewMeetingLocationType.None: {
      return <NoneIcon data-testid={dataTestId} color="action" />;
    }

    default:
      return null;
  }
};
